import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Breadcrumbs from "../../../components/breadcrumbs";
import NavBar from "../../../components/navbar";
import Spinner from "../../../components/spinner";
import Paginator from "../../../components/paginator";
import DropdownOptions from "../../../components/dropdownOptions";
import RofProgressionChart from "../../../components/RofProgressionChart";

import searchIcon from "../../../assets/images/search.svg";
import checkedCircle from "../../../assets/images/check-circle.svg";
import chevronUpIcon from "../../../assets/images/evalModules/chevron-up_base.svg";
import chevronRight from "../../../assets/images/evalModules/chevron-right_base.svg";
import chevronUpSelectedIcon from "../../../assets/images/evalModules/chevron-up_base.svg";
import chevronRightSelected from "../../../assets/images/evalModules/chevron-right_base.svg";
import moreBlue from "../../../assets/images/more-blue.svg";
import moreBlueSelected from "../../../assets/images/more-blue-selected-grey.svg";
import noteTextIcon from "../../../assets/images/note-text.svg";

import { parseDateFromDatetime } from "../../../utils/dataAndTime";
import useSearch from "hooks/useSearch";
import useOnClickOutside from "hooks/useOnClickOutside";

import {
  getRofServices,
  getRofLinkForEvaluation,
  getAssessmentScores,
} from "../../../utils/rofClient";
import { getPatientEvaluations } from "../../../redux/actions/patients";

const ITEMS_PER_PAGE = 10;

const scoreBadge = (scoreText) => {
  const s = String(scoreText || "").toLowerCase();
  if (s === "low") return { text: "Low", className: "badge--low" };
  if (s === "moderate" || s === "medium") return { text: "Moderate", className: "badge--moderate" };
  if (s === "high") return { text: "High", className: "badge--high" };
  return { text: "—", className: "badge--muted" };
};

const riskBadge = (pct) => {
  if (pct == null) return { text: "—", className: "badge--muted" };
  if (pct <= 35) return { text: "Low", className: "badge--low" };
  return { text: "High", className: "badge--high" };
};

// helpers 
async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let idx = 0;
  async function worker() {
    while (idx < items.length) {
      const cur = idx++;
      results[cur] = await mapper(items[cur], cur);
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, worker);
  await Promise.all(workers);
  return results;
}
function sortRows(rows, orderBy) {
  if (!orderBy) return rows;
  const byNameAsc = (a, b) => (a.name || "").localeCompare(b.name || "");
  const byNameDesc = (a, b) => -byNameAsc(a, b);
  const byDateAsc = (a, b) => new Date(a.dateISO) - new Date(b.dateISO);
  const byDateDesc = (a, b) => -byDateAsc(a, b);
  switch (orderBy) {
    case "name":
      return [...rows].sort(byNameAsc);
    case "name_inv":
      return [...rows].sort(byNameDesc);
    case "date":
      return [...rows].sort(byDateAsc);
    case "date_inv":
    default:
      return [...rows].sort(byDateDesc);
  }
}

/** Fetch only the global score bits we need (pct/label/components).
 *  Global score now lives on the assessment object itself. */
const getGlobalScoreOnly = async (services, assessmentId) => {
  const assessSvc =
    services?.assessmentService ||
    services?.AssessmentService ||
    services?.services?.AssessmentService;

  if (assessSvc?.getAssessment) {
    try {
      const a = await assessSvc.getAssessment(assessmentId);
      const pct =
        typeof a?.global_probability === "number"
          ? Math.round(a.global_probability * 100)
          : null;
      const label = a?.global_score ?? null;
      const components = a?.components ?? null;
      return { pct, label, components };
    } catch {}
  }

  return { pct: null, label: null, components: null };
};

const RofReports = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { patient_id } = useParams();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [detailsById, setDetailsById] = useState({});
  const [loadingDetailsIds, setLoadingDetailsIds] = useState(() => new Set());
  const [menuEvalId, setMenuEvalId] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [scoresLoaded, setScoresLoaded] = useState(false);

  const menuRef = useRef(null);
  useOnClickOutside([menuRef], () => setMenuEvalId(null));

  const servicesRef = useRef(null);

  const sdkPatientIdFromRedux = useSelector(
    (s) =>
      s.patientHome?.rof_sdk_patient_id ??
      s.patientHome?.rof_sdk_patientId ??
      s.patientHome?.rof_sdk_patient?.id ??
      null
  );
  
  const noopFetch = useCallback(() => {}, []);
  const { searchOptions, updateSearchText, updatePage, updateOrderBy } =
    useSearch(noopFetch, 300, "rofReports");

  const toggleOpen = (id) => {
    const key = String(id);
    setOpenIds((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
    if (!detailsById[key]?.modules) ensureDetails(key);
  };

  // items for progression chart
  const progressionItems = useMemo(() => {
    if (!rows?.length) return [];
    const ordered = [...rows]
      .filter((r) => r?.dateISO && !Number.isNaN(+new Date(r.dateISO)))
      .sort((a, b) => new Date(a.dateISO) - new Date(b.dateISO));

    return ordered
      .map((r) => {
        const d = detailsById[String(r.id)];
        const pct =
          typeof d?.rofPct === "number" ? Math.round(d.rofPct) : null;

        // Prefer components-based completion
        let completed = 1;
        if (typeof d?.completedFromComponents === "number") {
          completed = Math.max(1, Math.min(3, d.completedFromComponents));
        } else {
          // Fallback: infer from loaded modules if any
          const mods = d?.modules || [];
          const hasDone = (key) =>
            mods.some(
              (m) =>
                m.key === key &&
                (m.medicalCheck === true || (m.result != null && m.result !== "—"))
            );
          let c = 0;
          if (hasDone("sts")) c++;
          if (hasDone("tug")) c++;
          if (hasDone("medical_history")) c++;
          if (c > 0) completed = Math.min(3, c);
        }

        return { date: r.dateISO, pct, completed };
      })
      .filter((it) => typeof it.pct === "number");
  }, [rows, detailsById]);

  const prefetchGlobalScores = useCallback(
    async (assessments) => {
      setScoresLoaded(false);
      if (!assessments?.length) {
        setScoresLoaded(true);
        return;
      }

      const services = servicesRef.current || (await getRofServices());
      servicesRef.current = services;

      const countAvailable = (components) => {
        if (!components || typeof components !== "object") return null;
        const vals = Object.values(components);
        const avail = vals.filter((v) => String(v).toLowerCase() === "available").length;
        return avail; // may be 0..n
      };

      const results = await mapWithConcurrency(assessments, 6, async (row) => {
        const { pct, label, components } = await getGlobalScoreOnly(services, row.id);
        const availableCount = countAvailable(components);
        return {
          id: String(row.id),
          pct: pct,
          label,
          components,
          completedFromComponents:
            typeof availableCount === "number" ? Math.min(3, Math.max(0, availableCount)) : null,
        };
      });

      setDetailsById((prev) => {
        const next = { ...prev };
        for (const { id, pct, label, components, completedFromComponents } of results) {
          const old = next[id] || {};
          next[id] = {
            ...old,
            rofPct: pct ?? old.rofPct ?? null,
            globalLabel: label ?? old.globalLabel ?? null,
            globalComponents: components ?? old.globalComponents ?? null,
            completedFromComponents:
              completedFromComponents ?? old.completedFromComponents ?? null,
          };
        }
        return next;
      });

      setScoresLoaded(true);
    },
    []
  );

  const ensureDetails = useCallback(
    async (assessmentIdStr) => {
      const key = String(assessmentIdStr);
      if (detailsById[key]?.modules) return;

      setLoadingDetailsIds((prev) => {
        const n = new Set(prev);
        n.add(key);
        return n;
      });

      try {
        const scores = await getAssessmentScores(key);
        const num = (v) => typeof v === "number" && !Number.isNaN(v);
        const modules = [];
        const hasMedical = scores?.medicalHistory?.raw != null;

        if (hasMedical) {
          modules.push({ key: "medical_history", label: "Medical History", medicalCheck: true });
        }

        const stsUnableToPerform = scores?.sts?.raw?.unable_to_perform === true;
        const hasSTS =
          stsUnableToPerform ||
          num(scores?.sts?.raw?.duration_s) ||
          num(scores?.sts?.raw?.time_s) ||
          num(scores?.sts?.raw?.time) ||
          num(scores?.sts?.raw?.score) ||
          num(scores?.sts?.value);
        if (hasSTS) {
          modules.push({
            key: "sts",
            label: "STS 5 reps",
            result: stsUnableToPerform
              ? "---"
              : num(scores?.sts?.raw?.duration_s)
              ? `${scores.sts.raw.duration_s}s`
              : num(scores?.sts?.value)
              ? `${scores.sts.value}s`
              : "—",
            badge: stsUnableToPerform
              ? scoreBadge("high")
              : scoreBadge(scores?.sts?.raw?.sts_score ?? scores?.sts?.raw?.score),
            noteText: scores?.sts?.raw?.note_text || null,
          });
        }

        if (num(scores?.sts?.raw?.muscle_power_value)) {
          modules.push({
            key: "muscle_power",
            label: "Muscle Power",
            result: scores.sts.raw.muscle_power_value.toFixed(2),
            badge: scoreBadge(scores?.sts?.raw?.muscle_power_score),
          });
        }

        const tugUnableToPerform = scores?.tug?.raw?.unable_to_perform === true;
        const hasTUG =
          tugUnableToPerform ||
          num(scores?.tug?.raw?.duration_s) ||
          num(scores?.tug?.raw?.time_s) ||
          num(scores?.tug?.raw?.time) ||
          num(scores?.tug?.raw?.score) ||
          num(scores?.tug?.value);
        if (hasTUG) {
          modules.push({
            key: "tug",
            label: "TUG",
            result: tugUnableToPerform
              ? "---"
              : num(scores?.tug?.raw?.duration_s)
              ? `${scores.tug.raw.duration_s}s`
              : num(scores?.tug?.value)
              ? `${scores.tug.value}s`
              : "—",
            badge: tugUnableToPerform
              ? scoreBadge("high")
              : scoreBadge(scores?.tug?.raw?.score),
            noteText: scores?.tug?.raw?.note_text || null,
          });
        }

        const slsUnableToPerform = scores?.sls?.raw?.unable_to_perform === true;
        const hasSLS =
          slsUnableToPerform ||
          num(scores?.sls?.raw?.duration_s) ||
          num(scores?.sls?.raw?.time_s) ||
          num(scores?.sls?.raw?.time) ||
          num(scores?.sls?.raw?.score) ||
          num(scores?.sls?.value);
        if (hasSLS) {
          modules.push({
            key: "sls",
            label: "SLS",
            result: slsUnableToPerform
              ? "---"
              : num(scores?.sls?.raw?.duration_s)
              ? `${scores.sls.raw.duration_s}s`
              : num(scores?.sls?.value)
              ? `${scores.sls.value}s`
              : "—",
            badge: slsUnableToPerform
              ? scoreBadge("high")
              : scoreBadge(scores?.sls?.raw?.score),
            noteText: scores?.sls?.raw?.note_text || null,
          });
        }

        const shortFesUnableToPerform = scores?.shortFes?.raw?.unable_to_perform === true;
        const hasShortFES =
          shortFesUnableToPerform ||
          num(scores?.shortFes?.raw?.total) ||
          num(scores?.shortFes?.raw?.score) ||
          num(scores?.shortFes?.value);
        if (hasShortFES) {
          modules.push({
            key: "short_fes",
            label: "Short FES",
            result: shortFesUnableToPerform
              ? "---"
              : num(scores?.shortFes?.raw?.total)
              ? `${scores.shortFes.raw.total}`
              : `${scores.shortFes.value}`,
            badge: shortFesUnableToPerform
              ? scoreBadge("high")
              : scoreBadge(scores?.shortFes?.raw?.score),
          });
        }

        setDetailsById((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            stsSec: scores?.sts?.raw?.duration_s ?? null,
            tugSec: scores?.tug?.raw?.duration_s ?? null,
            modules, // may be empty -> UI shows "No modules available"
            raw: scores,
          },
        }));
      } catch (e) {
        setDetailsById((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            stsSec: null,
            tugSec: null,
            modules: [], // show "No modules available"
            raw: prev[key]?.raw ?? null,
          },
        }));
      } finally {
        setLoadingDetailsIds((prev) => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        });
      }
    },
    [detailsById]
  );

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      try {
        const services = await getRofServices();
        servicesRef.current = services;

        const res = await dispatch(
          getPatientEvaluations(patient_id, { page: 1, items_per_page: 1000 })
        );
        const evals = res?.payload?.data?.data?.obj_list ?? [];

        const sdkToEval = new Map();

        let sdkPatientId = sdkPatientIdFromRedux ?? null;

        if (!sdkPatientId && evals.length) {
          const limit = 6;
          let idx = 0;
          let found = null;

          async function worker() {
            while (!found && idx < evals.length) {
              const cur = idx++;
              const ev = evals[cur];
              try {
                const link = await getRofLinkForEvaluation(ev.id);
                if (link?.sdk_patient_id != null) {
                  sdkToEval.set(String(link.sdk_assessment_id), ev);
                  found = link.sdk_patient_id;
                }
              } catch {}
            }
          }
          await Promise.all(
            Array.from({ length: Math.min(limit, evals.length) }, worker)
          );
          sdkPatientId = found ?? null;
        }

        if (!sdkPatientId) {
          if (alive) setRows([]);
          setScoresLoaded(true);
          return;
        }

        let assessments = [];
        try {
          assessments = await services.assessmentService.getAssessmentsByPatient(
            sdkPatientId
          );
        } catch (err) {
          try {
            const all = await services.assessmentService.getAssessments();
            assessments = (all || []).filter(
              (a) => String(a.patient) === String(sdkPatientId)
            );
          } catch {
            assessments = [];
          }
        }

        const assessIdSet = new Set(assessments.map((a) => String(a.id)));
        const needLookup = evals.filter(
          (ev) => ![...sdkToEval.values()].includes(ev)
        );
        if (needLookup.length) {
          const linkResults = await mapWithConcurrency(
            needLookup,
            6,
            async (ev) => {
              try {
                const link = await getRofLinkForEvaluation(ev.id);
                if (link && String(link.sdk_patient_id) === String(sdkPatientId)) {
                  const aid = String(link.sdk_assessment_id);
                  if (assessIdSet.has(aid)) return { ev, aid };
                }
              } catch {}
              return null;
            }
          );
          linkResults
            .filter(Boolean)
            .forEach(({ ev, aid }) => sdkToEval.set(aid, ev));
        }

        const composed = assessments.map((a) => {
          const matchedEval = sdkToEval.get(String(a.id));
          const name = matchedEval?.name ?? t("Risk of Fall");
          const dateISO = matchedEval?.date ?? a?.created_at ?? null;
          const dateStr = dateISO ? parseDateFromDatetime(dateISO) : "—";
          const tags = matchedEval?.tags ?? [];
          return {
            id: String(a.id),
            evalId: matchedEval?.id ?? null,
            name,
            tags,
            dateStr,
            dateISO,
            mapping: { sdk_patient_id: sdkPatientId, sdk_assessment_id: a.id },
          };
        });

        if (!alive) return;

        setRows(composed);

        await prefetchGlobalScores(composed);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [dispatch, patient_id, sdkPatientIdFromRedux, t, prefetchGlobalScores]);

  // search + pagination + sorting
  const filtered = useMemo(() => {
    const q = (searchOptions?.q || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.name || "").toLowerCase().includes(q));
  }, [rows, searchOptions?.q]);

  const sorted = useMemo(
    () => sortRows(filtered, searchOptions?.orderBy || "date_inv"),
    [filtered, searchOptions?.orderBy]
  );

  const perPage = ITEMS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const currPage = Math.min(searchOptions?.page || 1, totalPages);
  const start = (currPage - 1) * perPage;
  const paged = sorted.slice(start, start + perPage);

  const toggleNameOrder = () =>
    updateOrderBy(
      searchOptions?.orderBy === "name"
        ? "name_inv"
        : searchOptions?.orderBy === "name_inv"
        ? "date_inv"
        : "name"
    );

  const toggleDateOrder = () =>
    updateOrderBy(searchOptions?.orderBy === "date_inv" ? "date" : "date_inv");

  const rowMenuButtons = (row) => [
    {
      text: <span className="regular-text">{t("buttons.view_details")}</span>,
      action: () => {
        if (!row.evalId) return;
        setMenuEvalId(null);
        history.push(`/patient/${patient_id}/rof_assessment/${row.evalId}`, {
          rofLink: row.mapping,
        });
      },
    },
  ];

  const chartLoading = !scoresLoaded && (rows?.length ?? 0) >= 0;

  return (
    <div className="col patient-container reports-page position-relative">
      <div className="row-space-between">
        <Breadcrumbs />
        <NavBar />
      </div>

      <div className="flex-d-column align-items-start mb-6">
        <div className="title-container mb-0">
          <h4>{t("titles.reports")}</h4>
        </div>
      </div>

      <div className="evaluations-module-wrapper rof-reports-wrapper">
        <div className="evaluations-module-container">
          <div className="row-space-between">
            <span className="small-title">{t("titles.reports")}</span>
          </div>

          <RofProgressionChart
            items={progressionItems}
            loading={chartLoading} 
            title={t("rof.progression_chart.title")}
          />

          {/* search */}
          <div className="row search-wrapper">
            <div className="col">
              <div style={{ position: "relative" }}>
                <input
                  value={searchOptions?.q || ""}
                  onChange={(e) => updateSearchText(e.target.value)}
                  type="text"
                  className="search-input w-100"
                  placeholder={t("placeholders.search_evaluations")}
                />
                <img alt="search-icon" className="search-icon" src={searchIcon} />
              </div>
            </div>
          </div>

          {/* list */}
          <div className="evaluations-list">
            <div className="row list-label eval-list-labels-wrapper">
              <div
                className="col-6 d-flex align-item-center"
                style={{ cursor: "pointer" }}
                onClick={toggleNameOrder}
              >
                <div>{t("patient.evaluations.name_short")}</div>
                <div
                  className={`order-img-container ${
                    searchOptions?.orderBy === "name" ||
                    searchOptions?.orderBy === "name_inv"
                      ? "active"
                      : ""
                  }`}
                >
                  {searchOptions?.orderBy !== "name" && (
                    <img
                      src={
                        searchOptions?.orderBy === "name_inv"
                          ? chevronUpSelectedIcon
                          : chevronUpIcon
                      }
                      alt="Order by Name"
                    />
                  )}
                  {searchOptions?.orderBy !== "name_inv" && (
                    <img
                      src={
                        searchOptions?.orderBy === "name"
                          ? chevronRightSelected
                          : chevronRight
                      }
                      alt="Order by Name Invert"
                    />
                  )}
                </div>
              </div>

              <div className="col-2">{t("patient.evaluations.tags")}</div>

              <div
                className="col-2 d-flex align-item-center"
                style={{ cursor: "pointer" }}
                onClick={toggleDateOrder}
              >
                <div>{t("patient.evaluations.date")}</div>
                <div
                  className={`order-img-container ${
                    searchOptions?.orderBy === "date" ||
                    searchOptions?.orderBy === "date_inv"
                      ? "active"
                      : ""
                  }`}
                >
                  {searchOptions?.orderBy !== "date_inv" && (
                    <img
                      src={
                        searchOptions?.orderBy === "date"
                          ? chevronUpSelectedIcon
                          : chevronUpIcon
                      }
                      alt="Order from Old to New"
                    />
                  )}
                  {searchOptions?.orderBy !== "date" && (
                    <img
                      src={
                        searchOptions?.orderBy === "date_inv"
                          ? chevronRightSelected
                          : chevronRight
                      }
                      alt="Order from New to Old"
                    />
                  )}
                </div>
              </div>

              <div className="col-2">Score</div>
            </div>

            {/* rows */}
            {loading ? (
              <div style={{ height: 100 }}>
                <div className="loading-wrapper">
                  <Spinner type="big-blue" />
                </div>
              </div>
            ) : paged.length === 0 ? (
              <div className="medium-base-color-text message-wrapper">
                <p>{t("patient.evaluations.no_match")}</p>
              </div>
            ) : (
              paged.map((row) => {
                const key = String(row.id);
                const details = detailsById[key] || null;
                let pct = details?.rofPct ?? null;
                
                const gBadge = details?.globalLabel ? scoreBadge(details.globalLabel) : riskBadge(pct);
                const isOpen = openIds.has(key);
                const isLoadingDetails = loadingDetailsIds.has(key);

                return (
                  <div key={key} className="eval-group">
                    <div className="row eval-info-wrapper align-items-center">
                      <div className="col-6 regular-text black-color text-truncate d-flex align-items-center">
                        <img
                          src={isOpen ? chevronUpSelectedIcon : chevronRightSelected}
                          alt="toggle"
                          className="mr-2"
                          style={{ cursor: "pointer", width: 18, height: 18 }}
                          onClick={() => toggleOpen(key)}
                        />
                        <span>{row.name}</span>
                      </div>

                      <div className="col-2 tags-wrapper regular-text black-color">
                        {Array.isArray(row.tags) && row.tags.length > 0
                          ? row.tags.map((tag) => (
                              <div key={tag.id || tag.name} className="tag-container">
                                {tag.name ?? tag}
                              </div>
                            ))
                          : "-----"}
                      </div>

                      <div className="col-2 regular-text black-color">{row.dateStr}</div>

                      <div className="col-2 d-flex align-items-center">
                        {pct != null ? (
                          <>
                            <span className="mr-2">{`${pct}%`}</span>
                            <span className={`rof-badge ${gBadge.className}`}>
                              {gBadge.text}
                            </span>
                          </>
                        ) : (
                          <span className="mr-2">—</span>
                        )}

                        {row.evalId && (
                          <div
                            className="more-image-wrapper ml-auto"
                            style={{ marginLeft: "auto" }}
                          >
                            <img
                              ref={menuRef}
                              className="image-cursor"
                              onMouseOver={(e) =>
                                menuEvalId !== row.id &&
                                (e.currentTarget.src = moreBlueSelected)
                              }
                              onMouseOut={(e) =>
                                menuEvalId !== row.id &&
                                (e.currentTarget.src = moreBlue)
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuEvalId(menuEvalId === row.id ? null : row.id);
                              }}
                              src={menuEvalId === row.id ? moreBlueSelected : moreBlue}
                              alt="More"
                            />
                            {menuEvalId === row.id && (
                              <DropdownOptions
                                ref={menuRef}
                                buttons={rowMenuButtons(row)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {isOpen && (
                      <div className="row">
                        <div className="col-12">
                          <div className="eval-details-table">
                            {isLoadingDetails ? (
                              <div className="row eval-info-wrapper">
                                <div className="col-12">
                                  <div className="loading-wrapper" style={{ margin: "8px 0" }}>
                                    <Spinner type="small-blue" />
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="row list-label eval-details-header eval-list-labels-wrapper">
                                  <div className="col-6">{t('rof.module')}</div>
                                  <div className="col-3">{t('rof.result')}</div>
                                  <div className="col-3 score-col">{t('rof.individual_score')}</div>
                                </div>

                                {(details?.modules?.length ?? 0) === 0 ? (
                                  <div className="row eval-info-wrapper">
                                    <div className="col-12 medium-base-color-text" style={{ padding: "8px 0" }}>
                                      {t("patient.evaluations.no_modules_available")}
                                    </div>
                                  </div>
                                ) : (
                                  details.modules.map((m) => (
                                    <div key={m.key} className="row eval-info-wrapper">
                                      <div className="col-6 regular-text black-color d-flex align-items-center module-name-cell">
                                        <span className="module-label">{m.label}</span>
                                        <span className="note-icon-slot">
                                          {m.noteText && (
                                            <span className="note-icon-wrapper">
                                              <img
                                                src={noteTextIcon}
                                                alt="Note"
                                                className="note-icon"
                                              />
                                              <span className="note-tooltip">{m.noteText}</span>
                                            </span>
                                          )}
                                        </span>
                                      </div>

                                      {/* Result column */}
                                      <div className="col-3 regular-text black-color">
                                        {m.medicalCheck ? (
                                          <img src={checkedCircle} alt="Completed" style={{ width: 16, height: 16 }} />
                                        ) : (
                                          m.result
                                        )}
                                      </div>

                                      {/* Individual Score column */}
                                      <div className="col-3 regular-text black-color score-col">
                                        {m.medicalCheck ? (
                                          <img src={checkedCircle} alt="Completed" style={{ width: 16, height: 16 }} />
                                        ) : (
                                          <span className={`rof-badge ${m.badge.className}`}>{m.badge.text}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {!loading && totalPages > 1 && (
          <Paginator
            currPage={currPage}
            totalPages={totalPages}
            maxPages={3}
            changePage={updatePage}
          />
        )}
      </div>
    </div>
  );
};

export default RofReports;
